import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import Title from '@/components/commons/page-elements/Title';
import { onCompletedHandler, onErrorHandler } from '@/services/apollo';
import Page from '@/components/layout/page';
import { withAuth } from '@/components/with_auth';
import Searchbar from '@/components/commons/searchbar';
import Loader from '@/components/commons/loader';
import Dropdown from '@/components/commons/dropdown';
import WarningCard from '@/components/commons/warning_card';
import inboxStyles from '@/components/index/Index.module.css';

import ConfirmActionModal from '@/components/inbox/mail-rules/ConfirmActionModal';
import MailRulesTable, { DEFAULT_SORT_COLUMN, DEFAULT_SORT_DIRECTION } from '@/components/inbox/mail-rules/MailRulesTable';
import { FETCH_MAIL_RULES, deleteSelected, updateSelected } from '@/components/inbox/mail-rules/Actions';
import { PAGE_SIZE_OPTIONS, ACTIONS } from '@/components/inbox/mail-rules/Constants';
import { selectedMailRules, mailRulesTablePageSizeOptions } from '@/components/inbox/mail-rules/State';
import styles from '@/components/inbox/mail-rules/MailRules.module.css';

function MailRules() {
    const [isError, setIsError] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const selectedRules = useRecoilValue(selectedMailRules);
    const setPageSizeOptions = useSetRecoilState(mailRulesTablePageSizeOptions);
    const [formError, setFormError] = useState('');

    const [mailRules, setMailRules] = useState([]);
    const [mailRulesHolder, setMailRulesHolder] = useState(null);
    const [sortColumn, setSortColumn] = useState(DEFAULT_SORT_COLUMN);
    const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);

    const [displayModal, setDisplayModal] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);

    const setLoading = () => false;

    const {
        loading: queryLoading, refetch,
    } = useQuery(FETCH_MAIL_RULES, {
        variables: {
            id: null,
            sortColumn,
            sortDirection,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        onError: onErrorHandler(setIsError),
        onCompleted: onCompletedHandler(setIsError, (data) => {
            const mailRulesData = data.user.mailRules.map((mailRule) => ({
                id: mailRule.id,
                senderEmail: mailRule.senderEmail,
                targetName: mailRule.targetName,
                accountEmail: mailRule.account.name,
                enabled: mailRule.enabled,
                deleteAction: mailRule.id,
            }));

            if (mailRulesData.length < 500) {
                setPageSizeOptions([...PAGE_SIZE_OPTIONS, {
                    value: mailRulesData.length,
                    label: 'all',
                }]);
            }
            setMailRulesHolder(mailRulesData);
        }),
    });

    useEffect(() => {
        if (mailRulesHolder) {
            if (searchTerm) {
                const convSearchTerm = searchTerm.toLowerCase();
                const filteredData = mailRulesHolder.filter(
                    (mailRule) => mailRule.senderEmail.toLowerCase().indexOf(convSearchTerm) >= 0
                        || mailRule.targetName.toLowerCase().indexOf(convSearchTerm) >= 0
                        || mailRule.accountEmail.toLowerCase().indexOf(convSearchTerm) >= 0,
                );
                setMailRules(filteredData);
            } else {
                setMailRules(mailRulesHolder);
            }
        }
    }, [searchTerm, mailRulesHolder]);

    const submitAction = async () => {
        switch (selectedAction) {
        case 0:
            updateSelected(selectedRules, true, refetch, setDisplayModal, setFormError);
            break;
        case 1:
            updateSelected(selectedRules, false, refetch, setDisplayModal, setFormError);
            break;
        case 2:
            deleteSelected(selectedRules, refetch, setDisplayModal, setFormError);
            break;
        default:
            break;
        }
    };

    const suffix = selectedRules.length ? `(${selectedRules.length} of ${mailRules.length})` : `(${mailRules.length})`;

    return (
        <Page>
            <div className={`flex flex-wrap fullHeightLessNavPage items-start ${inboxStyles.home}`}>
                <div className="w-full p-4 sm:p-8">
                    <div className="flex justify-between relative flex-col md:flex-row">
                        <div className="pr-6 text-left mb-8">
                            <Title title="Rules" subtitle="Rules powers the AI in Sixty. You shouldn't need to do anything here unless Support asks you to." />
                        </div>
                    </div>
                    {
                        formError !== '' && (
                            <div className="w-full my-[20px]">
                                <WarningCard message={formError} />
                            </div>
                        )
                    }
                    <div className={styles.tableContainer}>
                        <div className={styles.tableHeader}>
                            <p>
                                <b>Rules</b>
                                {` ${suffix}`}
                            </p>

                            <div className={styles.tableCommands}>
                                <div className={styles.searchContainer}>
                                    <Searchbar
                                        setSearchTerm={setSearchTerm}
                                        searchTerm={searchTerm}
                                    />
                                </div>

                                <div>
                                    <Dropdown
                                        onSelect={(value: string) => {
                                            setSelectedAction(value);
                                            setDisplayModal(true);
                                        }}
                                        selectionOptions={ACTIONS}
                                        clickableChildren
                                        disabled={selectedRules.length < 1}
                                        direction={1}
                                        withArrow
                                        isAction
                                    >
                                        <div className={styles.actionsButton}>
                                            <span className="pr-4">Actions</span>
                                        </div>
                                    </Dropdown>
                                </div>
                            </div>

                        </div>

                        <Loader isLoading={queryLoading && !mailRules.length} isError={isError}>
                            <MailRulesTable
                                setLoading={setLoading}
                                refetch={refetch}
                                mailRules={mailRules}
                                mailRulesHolder={mailRulesHolder}
                                sortColumn={sortColumn}
                                setSortColumn={setSortColumn}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}
                                formError={formError}
                                setFormError={setFormError}
                            />
                        </Loader>

                        <ConfirmActionModal
                            display={displayModal}
                            onSubmit={() => submitAction()}
                            closeModal={() => setDisplayModal(false)}
                            actionType={ACTIONS[selectedAction || '0'].label}
                        />
                    </div>

                </div>
            </div>
        </Page>

    );
}

export default withAuth(MailRules);
